import { Injectable, OnDestroy, Renderer2, RendererFactory2 } from '@angular/core';
import { Router } from '@angular/router';
import { Employee } from '@app/models/employee.model';
import { Service } from '@app/models/service.model';
import { PetApiService } from '@app/services/api/pet-api.service';
import { AppointmentService } from '@app/services/appointment.service';
import { FeatureFlagService, FeatureKey } from '@app/services/feature-flag.service';
import { LocalDbService } from '@app/services/local-db.service';
import { APPOINTMENT_REQUEST, CUSTOMER_ID, LocalStorageService, SITE, SITE_BOOKING_SETTINGS } from '@app/services/local-storage.service';
import { SiteService } from '@app/services/site.service';
import { Subject } from 'rxjs';
import tinycolor from 'tinycolor2';
import { ColorConfig, ThemeConfig } from '../../app/services/theme.service';

enum ThemeMainColorType {
    primaryColor = 'primaryColor',
}

@Injectable()
export class ThemeService implements OnDestroy {
    private static readonly THEME_VARIABLE_PREFIX = '--theme';
    // Allows listening to theme changes
    currentTheme: Subject<ThemeConfig> = new Subject();
    private readonly renderer: Renderer2;

    constructor(
        private rendererFactory: RendererFactory2,
        private localStorageService: LocalStorageService,
        private siteService: SiteService,
        private appointmentService: AppointmentService,
        private petApiService: PetApiService,
        private localDbCache: LocalDbService,
        private featureFlagService: FeatureFlagService,
        private router: Router
    ) {
        this.renderer = this.rendererFactory.createRenderer(null, null);
    }

    ngOnDestroy(): void {
        this.renderer?.destroy();
    }

    /**
     * The load method must return a Promise, since that will make the application wait in the APP_INITIALIZER DI token
     * After all the loading and set up is finished, we can proceed with rendering the application
     */
    async initialize(): Promise<any> {
        const url = new URL(window.location.href);
        let daySmartAccountID = [...url.searchParams].find(([k]) => k.toLowerCase() === 'dsid')?.[1] ?? null;
        const rescheduleGuid = url.searchParams.get('reschedule');
        if (daySmartAccountID) {
            try {
                this.subscribeToThemeChanges();
                daySmartAccountID = daySmartAccountID.trim().toUpperCase();
                const daySmartAccountIdFromStorage = this.localStorageService.getString(CUSTOMER_ID);
                const isDaySmartAccountIdDifferent = daySmartAccountIdFromStorage && daySmartAccountID !== daySmartAccountIdFromStorage;
                if (!daySmartAccountIdFromStorage || isDaySmartAccountIdDifferent) {
                    await this.localDbCache.clear();
                    // Clear localStorage but preserve site data if main.ts just populated it for current CID
                    const preservedSite = this.localStorageService.get(SITE);
                    this.localStorageService.clear();
                    if (preservedSite) {
                        this.localStorageService.set(SITE, preservedSite);
                    }
                }
                this.localStorageService.setString(CUSTOMER_ID, daySmartAccountID);
                if (rescheduleGuid) {
                    //grab appt item from dsb, if not found grab from posapi
                    let appointment;
                    try {
                        appointment = await this.appointmentService.getAppointmentByGuid(daySmartAccountID, rescheduleGuid).toPromise();
                    } catch (error) {
                        console.error('Error fetching appointment by GUID:', error);
                    }
                    if (!appointment || !appointment.employeeID || !appointment.itemID) {
                        try {
                            const res = await this.appointmentService
                                .getAppointmentFromPosApi(daySmartAccountID, rescheduleGuid)
                                .toPromise();
                            appointment = res?.appointment;
                        } catch (error) {
                            console.error('Error fetching appointment by GUID from api:', error);
                        }
                    }
                    if (appointment && appointment.employeeID && appointment.itemID) {
                        const employee = new Employee();
                        employee.id = appointment.employeeID || '';
                        const service = new Service();
                        service.id = appointment.itemID || '';
                        this.localStorageService.setNested(APPOINTMENT_REQUEST, 'service', [service]);
                        this.localStorageService.setNested(APPOINTMENT_REQUEST, 'employee', [employee]);
                        this.localStorageService.setNested(APPOINTMENT_REQUEST, 'rescheduleGuid', rescheduleGuid);
                    } else {
                        void this.router.navigate(['/booking/invalid']);
                    }
                }
                this.siteService.getSiteBookingSettings(daySmartAccountID).subscribe((result) => {
                    const siteBookingSettings = result;
                    const theme = siteBookingSettings.themeColor;
                    this.localStorageService.set(SITE_BOOKING_SETTINGS, siteBookingSettings);
                    this.currentTheme.next({ primaryColor: theme });
                });

                // Check if site data exists in localStorage (pre-populated by main.ts)
                let site = this.localStorageService.get(SITE) as any;
                if (!site || site.supportsPets === undefined) {
                    site = await this.siteService.getSite(daySmartAccountID);
                    this.localStorageService.set(SITE, site);
                }
                if (site.supportsPets) {
                    void this.petApiService.setPetTypeListToTheLS(daySmartAccountID);
                }
                await this.featureFlagService.initializeLaunchDarkly(site.supportsPets);
                const isRefreshAppointmentEnabled = this.featureFlagService.getFeatureFlag(FeatureKey.DSBRefreshAppointments);
                if (isRefreshAppointmentEnabled && url.pathname === '/booking/service') {
                    this.appointmentService.refreshAppointments(daySmartAccountID).subscribe();
                }
            } catch (error: any) {
                console.error(error);
                let isSignatureTimestampError = false;
                if (error.networkError?.name === 'ServerError' && (error.networkError as any).result?.errors?.length) {
                    (error.networkError as any).result.errors.map((error: { message: string }) => {
                        if (error.message?.startsWith('Signature not yet current') || error.message?.startsWith('Signature expired')) {
                            isSignatureTimestampError = true;
                        }
                    });
                }
                if (isSignatureTimestampError) {
                    void this.router.navigate(['/booking/incorrectTime']);
                } else {
                    if (error.message.includes(`A site with the provided DaySmart account id doesn't exist`)) {
                        this.localStorageService.remove(CUSTOMER_ID);
                    }
                    void this.router.navigate(['/booking/invalid']);
                }
            }
        } else {
            console.error('No daySmartAccountID');
            void this.router.navigate(['/booking/invalid']);
        }
    }

    private subscribeToThemeChanges(): void {
        this.currentTheme.subscribe((themeConfig: ThemeConfig) => {
            this.setupMainPalettes(themeConfig);
        });
    }

    /**
     * This method will generate the theme palette
     * @param themeConfig
     * @private
     */
    private setupMainPalettes(themeConfig: ThemeConfig): void {
        const selectedColorValue: string = themeConfig.primaryColor;

        // Should be for example: --theme-primary or --theme-accent etc..
        const variableName: string = this.prependVariableName(this.convertCamelCaseToKebabCase(ThemeMainColorType.primaryColor));

        // Generate the palette colors
        const colorPalette: Array<ColorConfig> = this.generateColorPalette(selectedColorValue);

        colorPalette.forEach((colorConfig: ColorConfig) => {
            // Destructure the color config
            const { colorVariant, colorHexValue, shouldHaveDarkContrast } = colorConfig;

            // Set the color variable
            const colorVariableName = `${variableName}-${colorVariant}`;
            this.setColorVariable(colorVariableName, colorHexValue);

            // Set the contrast color
            const contrastedColorVariableName = `${variableName}-contrast-${colorVariant}`;
            const contrastedColorValue = shouldHaveDarkContrast ? 'rgba(0, 0, 0, 0.87)' : '#fff';
            this.setColorVariable(contrastedColorVariableName, contrastedColorValue);
        });
    }

    /**
     * This method generates a color palette comprised of 14 main and 14 contrast colors
     * It will allow us to have different shades of some color and we can use all of those shades in our components via css.
     * @param hexColor
     * @private
     */
    private generateColorPalette(hexColor: string): Array<ColorConfig> {
        const baseLight = tinycolor('#ffffff');
        const baseDark = this.multiply(tinycolor(hexColor).toRgb(), tinycolor(hexColor).toRgb());
        const baseTriad = tinycolor(hexColor).tetrad();

        return [
            this.mapColorConfig(tinycolor.mix(baseLight, hexColor, 12), '50'),
            this.mapColorConfig(tinycolor.mix(baseLight, hexColor, 30), '100'),
            this.mapColorConfig(tinycolor.mix(baseLight, hexColor, 50), '200'),
            this.mapColorConfig(tinycolor.mix(baseLight, hexColor, 70), '300'),
            this.mapColorConfig(tinycolor.mix(baseLight, hexColor, 85), '400'),
            this.mapColorConfig(tinycolor.mix(baseLight, hexColor, 100), '500'),
            this.mapColorConfig(tinycolor.mix(baseDark, hexColor, 87), '600'),
            this.mapColorConfig(tinycolor.mix(baseDark, hexColor, 70), '700'),
            this.mapColorConfig(tinycolor.mix(baseDark, hexColor, 54), '800'),
            this.mapColorConfig(tinycolor.mix(baseDark, hexColor, 25), '900'),
            this.mapColorConfig(tinycolor.mix(baseDark, baseTriad[3], 15).saturate(80).lighten(65), 'A100'),
            this.mapColorConfig(tinycolor.mix(baseDark, baseTriad[3], 15).saturate(80).lighten(55), 'A200'),
            this.mapColorConfig(tinycolor.mix(baseDark, baseTriad[3], 15).saturate(100).lighten(45), 'A400'),
            this.mapColorConfig(tinycolor.mix(baseDark, baseTriad[3], 15).saturate(100).lighten(40), 'A700'),
        ];
    }

    /**
     * Map the color and its variant to something that we understand
     * Also check if we need to use a light or dark contrast color
     * @param tinyColorInstance
     * @param colorVariant
     * @private
     */
    private mapColorConfig(tinyColorInstance: tinycolor.Instance, colorVariant: string): ColorConfig {
        return {
            colorVariant,
            colorHexValue: tinyColorInstance.toHexString(),
            shouldHaveDarkContrast: tinyColorInstance.isLight(),
        };
    }

    private multiply(rgb1: tinycolor.ColorFormats.RGB, rgb2: tinycolor.ColorFormats.RGB): tinycolor.Instance {
        rgb1.r = Math.floor((rgb1.r * rgb2.r) / 255);
        rgb1.g = Math.floor((rgb1.g * rgb2.g) / 255);
        rgb1.b = Math.floor((rgb1.b * rgb2.b) / 255);
        const { r, g, b } = rgb1;

        return tinycolor(`rgb ${r} ${g} ${b}`);
    }

    private prependVariableName(key: string): string {
        return `${ThemeService.THEME_VARIABLE_PREFIX}-${key}`;
    }

    /**
     * Change a camelCase variable to a kebab case
     * e.g: primaryColor -> primary-color
     * @param key
     * @private
     */
    private convertCamelCaseToKebabCase(key: string): string {
        return key.replace(/([a-z0-9]|(?=[A-Z]))([A-Z])/g, '$1-$2').toLowerCase();
    }

    private setColorVariable(variable: string, color: string): void {
        document.documentElement.style.setProperty(variable, color);
    }
}
